import { useMemo, useState, type ReactNode } from "react";
import {
  FiActivity,
  FiCalendar,
  FiCheck,
  FiClock,
  FiEdit2,
  FiGitBranch,
  FiMoreVertical,
  FiPauseCircle,
  FiPlay,
  FiPlus,
  FiRefreshCw,
  FiRepeat,
  FiSearch,
  FiSettings,
  FiSlash,
  FiX,
  FiZap,
} from "react-icons/fi";

import {
  PageContainer,
  RuntimeDiagnosticBanner,
  type ComponentDiagnostic,
} from "@swot/base-components";

import { CmtsEnterpriseModal } from "@components/CmtsEnterpriseModal.js";
import { CmtsPageHintPanel } from "@components/hints/index.js";
import {
  buildCmtsAdminDependencyImpact,
  type CmtsAdminDependencyImpact,
} from "@cmts/admin/index.js";
import { normalizeUserSafeApiError } from "@cmts/api/apiErrorNormalizer.js";

export type CmtsScheduledJobRow = Record<string, unknown> & {
  id?: string | undefined;
  jobId?: string | undefined;
  jobCode: string;
  jobName: string;
  jobType: string;
  cronExpression: string;
  maxRetryCount: number;
  timeoutSeconds: number;
  isActive: boolean;
  status: string;
  lastRunAt?: string | undefined;
  nextRunAt?: string | undefined;
};

type ScheduledJobForm = {
  jobCode: string;
  jobName: string;
  jobType: string;
  cronExpression: string;
  maxRetryCount: string;
  timeoutSeconds: string;
  isActive: boolean;
};

type Props = {
  rows?: readonly Record<string, unknown>[];
  reminders?: readonly Record<string, unknown>[];
  diagnostics?: readonly ComponentDiagnostic[];
  onUpsert?: (payload: Record<string, unknown>) => void | Promise<void>;
  onDeactivate?: (row: Record<string, unknown>) => void | Promise<void>;
  onReactivate?: (row: Record<string, unknown>) => void | Promise<void>;
  onTest?: (row: Record<string, unknown>) => void | Promise<void>;
  dependencyData?: Record<string, readonly Record<string, unknown>[]>;
  onRefresh?: () => void | Promise<void>;
};

const EMPTY_FORM: ScheduledJobForm = {
  jobCode: "",
  jobName: "",
  jobType: "SLA_EVALUATION",
  cronExpression: "0 * * * *",
  maxRetryCount: "3",
  timeoutSeconds: "600",
  isActive: true,
};

const JOB_TYPES = [
  {
    value: "SLA_EVALUATION",
    label: "SLA Evaluation",
  },
  {
    value: "REMINDER_DISPATCH",
    label: "Reminder Dispatch",
  },
  {
    value: "EXPORT_CLEANUP",
    label: "Export Cleanup",
  },
  {
    value: "OPERATION_RETRY",
    label: "Operation Retry",
  },
  {
    value: "MESSAGE_DIGEST",
    label: "Message Digest",
  },
  {
    value: "NOTIFICATION_DISPATCH",
    label: "Notification Dispatch",
  },
  {
    value: "SLA_WARNING",
    label: "SLA Warning",
  },
  {
    value: "TASK_OVERDUE_CHECK",
    label: "Task Overdue Check",
  },
] as const;

function readText(value: unknown, fallback = "—"): string {
  const result = String(value ?? "").trim();
  return result || fallback;
}

function readNumber(value: unknown, fallback: number): number {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : fallback;
}

function readActive(value: unknown): boolean {
  if (typeof value === "boolean") {
    return value;
  }

  return !["false", "0", "inactive", "disabled"].includes(
    String(value ?? "")
      .trim()
      .toLowerCase(),
  );
}

function normalizeJob(row: Record<string, unknown>): CmtsScheduledJobRow {
  const isActive = readActive(row.isActive ?? row.active ?? row.status);

  const jobCode = readText(row.jobCode ?? row.code, "UNNAMED_JOB");

  const jobName = readText(
    row.jobName ?? row.name ?? row.label ?? row.job,
    "Scheduled job",
  );

  return {
    ...row,

    id: row.id ? String(row.id) : undefined,

    jobId: row.jobId ? String(row.jobId) : undefined,

    code: jobCode,
    label: jobName,
    jobCode,
    jobName,

    jobType: readText(row.jobType ?? row.type, "SLA_EVALUATION").toUpperCase(),

    cronExpression: readText(
      row.cronExpression ?? row.cron ?? row.schedule,
      "0 * * * *",
    ),

    maxRetryCount: readNumber(
      row.maxRetryCount ?? row.maxRetries ?? row.retryCount,
      3,
    ),

    timeoutSeconds: readNumber(row.timeoutSeconds ?? row.timeout, 600),

    isActive,
    active: isActive,
    status: isActive ? "Active" : "Inactive",

    lastRunAt: readText(row.lastRunAt ?? row.lastRun, ""),

    nextRunAt: readText(row.nextRunAt ?? row.nextRun, ""),
  };
}

function jobTypeLabel(value: string): string {
  const knownType = JOB_TYPES.find((item) => item.value === value);

  if (knownType) {
    return knownType.label;
  }

  return value
    .toLowerCase()
    .replaceAll("_", " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function jobTypeTone(value: string): string {
  if (value.includes("SLA")) {
    return "blue";
  }

  if (value.includes("REMINDER") || value.includes("NOTIFICATION")) {
    return "violet";
  }

  if (value.includes("EXPORT") || value.includes("CLEANUP")) {
    return "amber";
  }

  if (value.includes("RETRY") || value.includes("OVERDUE")) {
    return "rose";
  }

  return "slate";
}

function csvCell(value: unknown): string {
  return `"${String(value ?? "").replaceAll('"', '""')}"`;
}

function downloadJobsCsv(rows: readonly CmtsScheduledJobRow[]): void {
  const headers = [
    "Job Code",
    "Job Name",
    "Job Type",
    "Cron Expression",
    "Max Retries",
    "Timeout Seconds",
    "Status",
  ];

  const body = rows.map((row) =>
    [
      row.jobCode,
      row.jobName,
      jobTypeLabel(row.jobType),
      row.cronExpression,
      row.maxRetryCount,
      row.timeoutSeconds,
      row.status,
    ]
      .map(csvCell)
      .join(","),
  );

  const csv = [headers.map(csvCell).join(","), ...body].join("\n");

  const blob = new Blob([csv], {
    type: "text/csv;charset=utf-8",
  });

  const url = URL.createObjectURL(blob);

  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = `scheduled-jobs-${new Date()
    .toISOString()
    .slice(0, 10)}.csv`;

  anchor.click();
  URL.revokeObjectURL(url);
}

function formatDate(value: string | undefined): string {
  if (!value) {
    return "Not available";
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function ActionCard(props: {
  icon: ReactNode;
  title: string;
  description: string;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={[
        "cmts-job-action-card",
        props.danger ? "cmts-job-action-card--danger" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      disabled={props.disabled}
      onClick={props.onClick}
    >
      <span className="cmts-job-action-card__icon" aria-hidden="true">
        {props.icon}
      </span>

      <span className="cmts-job-action-card__copy">
        <strong>{props.title}</strong>
        <small>{props.description}</small>
      </span>

      <span className="cmts-job-action-card__arrow" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

export function CmtsScheduledJobsPage(props: Props) {
  const jobs = useMemo(
    () => (props.rows ?? []).map(normalizeJob),
    [props.rows],
  );

  const [search, setSearch] = useState("");

  const [typeFilter, setTypeFilter] = useState("ALL");

  const [statusFilter, setStatusFilter] = useState<
    "ALL" | "ACTIVE" | "INACTIVE"
  >("ALL");

  const [actionsFor, setActionsFor] = useState<CmtsScheduledJobRow | null>(
    null,
  );

  const [formOpen, setFormOpen] = useState(false);

  const [editingRow, setEditingRow] = useState<CmtsScheduledJobRow | null>(
    null,
  );

  const [form, setForm] = useState<ScheduledJobForm>(EMPTY_FORM);

  const [impactOpen, setImpactOpen] = useState(false);

  const [impactRow, setImpactRow] = useState<CmtsScheduledJobRow | null>(null);

  const [impact, setImpact] = useState<CmtsAdminDependencyImpact | null>(null);

  const [detailsFor, setDetailsFor] = useState<CmtsScheduledJobRow | null>(
    null,
  );

  const [busy, setBusy] = useState(false);

  const [notice, setNotice] = useState<{
    tone: "success" | "danger";
    text: string;
  } | null>(null);

  const typeOptions = useMemo(
    () => [...new Set(jobs.map((row) => row.jobType))].sort(),
    [jobs],
  );

  const filteredJobs = useMemo(() => {
    const query = search.trim().toLowerCase();

    return jobs.filter((row) => {
      const matchesSearch =
        !query ||
        [row.jobCode, row.jobName, row.jobType, row.cronExpression].some(
          (value) => value.toLowerCase().includes(query),
        );

      const matchesType = typeFilter === "ALL" || row.jobType === typeFilter;

      const matchesStatus =
        statusFilter === "ALL" ||
        (statusFilter === "ACTIVE" && row.isActive) ||
        (statusFilter === "INACTIVE" && !row.isActive);

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [jobs, search, typeFilter, statusFilter]);

  const activeCount = jobs.filter((row) => row.isActive).length;

  const inactiveCount = jobs.length - activeCount;

  const retryEnabledCount = jobs.filter((row) => row.maxRetryCount > 0).length;

  const openCreate = () => {
    setEditingRow(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (row: CmtsScheduledJobRow) => {
    setEditingRow(row);

    setForm({
      jobCode: row.jobCode,
      jobName: row.jobName,
      jobType: row.jobType,
      cronExpression: row.cronExpression,
      maxRetryCount: String(row.maxRetryCount),
      timeoutSeconds: String(row.timeoutSeconds),
      isActive: row.isActive,
    });

    setActionsFor(null);
    setFormOpen(true);
  };

  const saveJob = async () => {
    if (
      !form.jobCode.trim() ||
      !form.jobName.trim() ||
      !form.cronExpression.trim()
    ) {
      setNotice({
        tone: "danger",
        text: "Job code, job name and cron expression are required.",
      });

      return;
    }

    setBusy(true);
    setNotice(null);

    try {
      if (!props.onUpsert) {
        throw new Error(
          "Scheduled job save is not connected to the repository.",
        );
      }

      const jobCode = form.jobCode.trim().toUpperCase();

      await props.onUpsert({
        ...(editingRow ?? {}),
        jobCode,
        code: jobCode,

        jobName: form.jobName.trim(),

        name: form.jobName.trim(),

        label: form.jobName.trim(),

        jobType: form.jobType,

        cronExpression: form.cronExpression.trim(),

        maxRetryCount: Math.max(0, Number(form.maxRetryCount) || 0),

        timeoutSeconds: Math.max(1, Number(form.timeoutSeconds) || 1),

        isActive: form.isActive,

        active: form.isActive,

        status: form.isActive ? "Active" : "Inactive",

        masterType: "scheduledJobs",
      });

      setFormOpen(false);

      setNotice({
        tone: "success",
        text: `${jobCode} saved successfully.`,
      });
    } catch (error) {
      setNotice({
        tone: "danger",
        text:
          normalizeUserSafeApiError(error).userMessage ||
          "Scheduled job save failed.",
      });
    } finally {
      setBusy(false);
    }
  };

  const runNow = async (row: CmtsScheduledJobRow) => {
    setBusy(true);
    setNotice(null);

    try {
      if (!props.onTest) {
        throw new Error("Run now is not connected to the repository.");
      }

      await props.onTest(row);

      setActionsFor(null);

      setNotice({
        tone: "success",
        text: `${row.jobName} was queued for immediate execution.`,
      });
    } catch (error) {
      setNotice({
        tone: "danger",
        text:
          normalizeUserSafeApiError(error).userMessage ||
          "Unable to run the scheduled job.",
      });
    } finally {
      setBusy(false);
    }
  };

  const toggleStatus = async (row: CmtsScheduledJobRow) => {
    setBusy(true);
    setNotice(null);

    try {
      if (row.isActive) {
        if (!props.onDeactivate) {
          throw new Error("Deactivate is not connected to the repository.");
        }

        await props.onDeactivate(row);
      } else {
        if (!props.onReactivate) {
          throw new Error("Reactivate is not connected to the repository.");
        }

        await props.onReactivate(row);
      }

      setActionsFor(null);
      setImpactOpen(false);

      setNotice({
        tone: "success",
        text: `${row.jobName} ${
          row.isActive ? "deactivated" : "reactivated"
        } successfully.`,
      });
    } catch (error) {
      setNotice({
        tone: "danger",
        text:
          normalizeUserSafeApiError(error).userMessage ||
          "Unable to change the job status.",
      });
    } finally {
      setBusy(false);
    }
  };

  const openImpact = (row: CmtsScheduledJobRow) => {
    const result = buildCmtsAdminDependencyImpact(
      row,
      props.dependencyData ?? {},
    );

    setImpactRow(row);
    setImpact(result);
    setActionsFor(null);
    setImpactOpen(true);
  };

  const refresh = async () => {
    setBusy(true);
    setNotice(null);

    try {
      if (!props.onRefresh) {
        throw new Error("Refresh is not connected to the repository.");
      }

      await props.onRefresh();

      setNotice({
        tone: "success",
        text: "Scheduled jobs refreshed from backend.",
      });
    } catch (error) {
      setNotice({
        tone: "danger",
        text: normalizeUserSafeApiError(error).userMessage || "Refresh failed.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageContainer
      title="Scheduled Job Administration"
      subtitle="Runtime scheduling, execution control, retries and dependency-aware job governance."
    >
      <div className="cmts-job-admin">
        <RuntimeDiagnosticBanner
          diagnostics={props.diagnostics ?? []}
          title="Scheduled job diagnostics"
        />

        <CmtsPageHintPanel defaultOpen={false}>
          Scheduled jobs drive background operational automation including SLA threshold evaluation, automated reminder dispatch, escalation triggers, and maintenance cleanups.
        </CmtsPageHintPanel>

        <section className="cmts-workspace-hero">
          <div>
            <p className="cmts-section-eyebrow">
              Automation &amp; Runtime Governance
            </p>
            <h2>Scheduled Job Administration</h2>
            <p>
              Control SLA evaluation, reminder delivery, cleanup and
              operational retries from one backend-authoritative workspace.
            </p>
          </div>

          <div className="cmts-page-actions">
            <button
              type="button"
              className="cmts-action-button"
              onClick={openCreate}
            >
              <FiPlus />
              Create Scheduled Job
            </button>

            <button
              type="button"
              className="cmts-secondary-button"
              disabled={busy}
              onClick={() => void refresh()}
            >
              <FiRefreshCw className={busy ? "cmts-job-spin" : ""} />
              Refresh
            </button>
          </div>
        </section>

        {notice ? (
          <div
            className={`cmts-job-notice cmts-job-notice--${notice.tone}`}
            role="status"
          >
            <span>{notice.text}</span>

            <button
              type="button"
              aria-label="Dismiss message"
              onClick={() => setNotice(null)}
            >
              <FiX />
            </button>
          </div>
        ) : null}

        <section
          className="cmts-job-admin__kpis"
          aria-label="Scheduled job summary"
        >
          <article className="cmts-job-kpi">
            <span className="cmts-job-kpi__icon cmts-job-kpi__icon--blue">
              <FiSettings />
            </span>

            <div>
              <small>Total Jobs</small>
              <strong>{jobs.length}</strong>
              <span>Configured definitions</span>
            </div>
          </article>

          <article className="cmts-job-kpi">
            <span className="cmts-job-kpi__icon cmts-job-kpi__icon--green">
              <FiActivity />
            </span>

            <div>
              <small>Active Jobs</small>
              <strong>{activeCount}</strong>
              <span>Running on schedule</span>
            </div>
          </article>

          <article className="cmts-job-kpi">
            <span className="cmts-job-kpi__icon cmts-job-kpi__icon--amber">
              <FiRepeat />
            </span>

            <div>
              <small>Retry Protected</small>
              <strong>{retryEnabledCount}</strong>
              <span>Automatic retry enabled</span>
            </div>
          </article>

          <article className="cmts-job-kpi">
            <span className="cmts-job-kpi__icon cmts-job-kpi__icon--rose">
              <FiPauseCircle />
            </span>

            <div>
              <small>Inactive Jobs</small>
              <strong>{inactiveCount}</strong>
              <span>Currently paused</span>
            </div>
          </article>
        </section>

        <section className="cmts-job-admin__controls">
          <div className="cmts-job-admin__filter-row">
            <div className="cmts-job-admin__type-pills">
              <button
                type="button"
                className={typeFilter === "ALL" ? "is-active" : ""}
                onClick={() => setTypeFilter("ALL")}
              >
                All Jobs
              </button>

              {typeOptions.slice(0, 5).map((type) => (
                <button
                  key={type}
                  type="button"
                  className={typeFilter === type ? "is-active" : ""}
                  onClick={() => setTypeFilter(type)}
                >
                  {jobTypeLabel(type)}
                </button>
              ))}
            </div>

            <div className="cmts-job-admin__search">
              <FiSearch />

              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by job code, name, type or cron..."
              />

              {search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch("")}
                >
                  <FiX />
                </button>
              ) : null}
            </div>
          </div>

          <div className="cmts-job-admin__subfilters">
            <span>
              <strong>Status:</strong>
            </span>

            {(["ALL", "ACTIVE", "INACTIVE"] as const).map((status) => (
              <button
                key={status}
                type="button"
                className={statusFilter === status ? "is-active" : ""}
                onClick={() => setStatusFilter(status)}
              >
                {status === "ALL"
                  ? "All"
                  : status === "ACTIVE"
                    ? "Active only"
                    : "Inactive only"}
              </button>
            ))}

            <span className="cmts-job-admin__result-count">
              {filteredJobs.length} result
              {filteredJobs.length === 1 ? "" : "s"}
            </span>

            <button
              type="button"
              className="cmts-job-admin__export"
              disabled={filteredJobs.length === 0}
              onClick={() => downloadJobsCsv(filteredJobs)}
            >
              Export CSV
            </button>
          </div>
        </section>

        <section className="cmts-job-table-card">
          <div className="cmts-job-table-scroll">
            <table className="cmts-job-table">
              <thead>
                <tr>
                  <th>Job & Code</th>
                  <th>Job Type</th>
                  <th>Schedule</th>
                  <th>Retry Policy</th>
                  <th>Timeout</th>
                  <th>Status</th>

                  <th className="cmts-job-table__actions-heading">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredJobs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="cmts-job-table__empty">
                      <FiCalendar />

                      <strong>No scheduled jobs matched your filters.</strong>

                      <span>
                        Clear the search or create a new scheduled job.
                      </span>
                    </td>
                  </tr>
                ) : (
                  filteredJobs.map((row) => (
                    <tr key={row.id ?? row.jobId ?? row.jobCode}>
                      <td>
                        <div className="cmts-job-name-cell">
                          <strong>{row.jobName}</strong>

                          <code>{row.jobCode}</code>
                        </div>
                      </td>

                      <td>
                        <span
                          className={`cmts-job-type cmts-job-type--${jobTypeTone(
                            row.jobType,
                          )}`}
                        >
                          {jobTypeLabel(row.jobType)}
                        </span>
                      </td>

                      <td>
                        <div className="cmts-job-cron">
                          <FiClock />

                          <code>{row.cronExpression}</code>
                        </div>
                      </td>

                      <td>
                        <span className="cmts-job-number">
                          <FiRepeat />
                          {row.maxRetryCount} retries
                        </span>
                      </td>

                      <td>
                        <span className="cmts-job-number">
                          {row.timeoutSeconds}s
                        </span>
                      </td>

                      <td>
                        <span
                          className={[
                            "cmts-job-status",
                            row.isActive
                              ? "cmts-job-status--active"
                              : "cmts-job-status--inactive",
                          ].join(" ")}
                        >
                          {row.isActive ? <FiCheck /> : <FiSlash />}

                          {row.status}
                        </span>
                      </td>

                      <td className="cmts-job-table__actions-cell">
                        <button
                          type="button"
                          className="cmts-job-actions-button"
                          onClick={() => setActionsFor(row)}
                        >
                          <FiMoreVertical />
                          Actions
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <CmtsEnterpriseModal
          open={Boolean(actionsFor)}
          title="Scheduled job actions"
          subtitle={
            actionsFor
              ? `${actionsFor.jobCode} • ${actionsFor.jobName}`
              : undefined
          }
          size="md"
          onClose={() => setActionsFor(null)}
        >
          {actionsFor ? (
            <div className="cmts-job-action-modal">
              <div className="cmts-job-action-modal__summary">
                <div>
                  <span>Current status</span>

                  <strong
                    className={
                      actionsFor.isActive ? "is-active" : "is-inactive"
                    }
                  >
                    {actionsFor.status.toUpperCase()}
                  </strong>
                </div>

                <div>
                  <span>Job type</span>

                  <strong>{jobTypeLabel(actionsFor.jobType)}</strong>
                </div>

                <div>
                  <span>Schedule</span>

                  <strong>{actionsFor.cronExpression}</strong>
                </div>
              </div>

              <div className="cmts-job-action-modal__grid">
                <ActionCard
                  icon={<FiZap />}
                  title="Run job now"
                  description="Queue an immediate backend execution for this job."
                  disabled={busy || !actionsFor.isActive}
                  onClick={() => void runNow(actionsFor)}
                />

                <ActionCard
                  icon={<FiEdit2 />}
                  title="Edit scheduled job"
                  description="Update job name, type, cron, retry and timeout settings."
                  onClick={() => openEdit(actionsFor)}
                />

                <ActionCard
                  icon={<FiGitBranch />}
                  title="View dependency impact"
                  description="Inspect connected SLA, notification and audit records."
                  onClick={() => openImpact(actionsFor)}
                />

                <ActionCard
                  icon={<FiCalendar />}
                  title="View schedule details"
                  description="Review cron, last run and next planned execution."
                  onClick={() => {
                    setDetailsFor(actionsFor);

                    setActionsFor(null);
                  }}
                />
              </div>

              <div className="cmts-job-action-modal__danger">
                <ActionCard
                  icon={actionsFor.isActive ? <FiPauseCircle /> : <FiPlay />}
                  title={
                    actionsFor.isActive
                      ? "Deactivate scheduled job"
                      : "Reactivate scheduled job"
                  }
                  description={
                    actionsFor.isActive
                      ? "Pause future executions for this job."
                      : "Restore this job to the active schedule."
                  }
                  danger={actionsFor.isActive}
                  disabled={busy}
                  onClick={() => {
                    if (actionsFor.isActive) {
                      openImpact(actionsFor);
                    } else {
                      void toggleStatus(actionsFor);
                    }
                  }}
                />
              </div>
            </div>
          ) : null}
        </CmtsEnterpriseModal>

        <CmtsEnterpriseModal
          open={formOpen}
          title={
            editingRow
              ? `Edit Scheduled Job: ${editingRow.jobCode}`
              : "Create Scheduled Job"
          }
          subtitle="Configure runtime execution, retry and timeout governance."
          size="lg"
          onClose={() => setFormOpen(false)}
          footer={
            <div className="cmts-job-modal-footer">
              <button
                type="button"
                className="cmts-job-modal-btn cmts-job-modal-btn--secondary"
                disabled={busy}
                onClick={() => setFormOpen(false)}
              >
                Cancel
              </button>

              <button
                type="button"
                className="cmts-job-modal-btn cmts-job-modal-btn--primary"
                disabled={busy}
                onClick={() => void saveJob()}
              >
                {busy
                  ? "Saving..."
                  : editingRow
                    ? "Save Changes"
                    : "Create Job"}
              </button>
            </div>
          }
        >
          <div className="cmts-job-form">
            <div className="cmts-job-form__grid">
              <label>
                Job Code *
                <input
                  value={form.jobCode}
                  disabled={Boolean(editingRow)}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      jobCode: event.target.value
                        .toUpperCase()
                        .replaceAll(" ", "_"),
                    }))
                  }
                  placeholder="e.g. COMM_SLA_WARNING_15MIN"
                />
              </label>

              <label>
                Job Name *
                <input
                  value={form.jobName}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      jobName: event.target.value,
                    }))
                  }
                  placeholder="e.g. Communication SLA Warning"
                />
              </label>

              <label>
                Job Type *
                <select
                  value={form.jobType}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      jobType: event.target.value,
                    }))
                  }
                >
                  {JOB_TYPES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Cron Expression *
                <input
                  value={form.cronExpression}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      cronExpression: event.target.value,
                    }))
                  }
                  placeholder="e.g. */15 * * * *"
                />
                <small>
                  Five-field cron: minute, hour, day, month, weekday.
                </small>
              </label>

              <label>
                Maximum Retry Count *
                <input
                  type="number"
                  min="0"
                  max="20"
                  value={form.maxRetryCount}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      maxRetryCount: event.target.value,
                    }))
                  }
                />
              </label>

              <label>
                Timeout (Seconds) *
                <input
                  type="number"
                  min="1"
                  max="86400"
                  value={form.timeoutSeconds}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      timeoutSeconds: event.target.value,
                    }))
                  }
                />
              </label>
            </div>

            <label className="cmts-job-form__toggle">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    isActive: event.target.checked,
                  }))
                }
              />

              <span>
                <strong>Enable scheduled execution</strong>

                <small>
                  This job can run using the configured cron schedule.
                </small>
              </span>
            </label>
          </div>
        </CmtsEnterpriseModal>

        <CmtsEnterpriseModal
          open={impactOpen}
          title="Dependency impact"
          subtitle={
            impactRow
              ? `${impactRow.jobCode} • ${impactRow.jobName}`
              : undefined
          }
          size="lg"
          onClose={() => setImpactOpen(false)}
          footer={
            impactRow ? (
              <div className="cmts-job-modal-footer">
                <button
                  type="button"
                  className="cmts-job-modal-btn cmts-job-modal-btn--secondary"
                  onClick={() => setImpactOpen(false)}
                >
                  Close
                </button>

                {impactRow.isActive ? (
                  <button
                    type="button"
                    className="cmts-job-modal-btn cmts-job-modal-btn--danger"
                    disabled={busy || impact?.canProceed === false}
                    onClick={() => void toggleStatus(impactRow)}
                  >
                    Deactivate Job
                  </button>
                ) : null}
              </div>
            ) : undefined
          }
        >
          {impact ? (
            <div className="cmts-job-impact">
              <div
                className={[
                  "cmts-job-impact__banner",
                  impact.canProceed
                    ? "cmts-job-impact__banner--clear"
                    : "cmts-job-impact__banner--blocked",
                ].join(" ")}
              >
                <FiGitBranch />

                <div>
                  <strong>
                    {impact.canProceed
                      ? "Dependency check completed"
                      : "Deactivation blocked"}
                  </strong>

                  <span>
                    {impact.canProceed
                      ? "No blocking active dependency was found."
                      : "Resolve blocking dependencies before deactivation."}
                  </span>
                </div>
              </div>

              <div className="cmts-job-impact__metrics">
                <div>
                  <span>Blocking</span>
                  <strong>{impact.blockingCount}</strong>
                </div>

                <div>
                  <span>Warnings</span>
                  <strong>{impact.warningCount}</strong>
                </div>

                <div>
                  <span>Replacement required</span>

                  <strong>{impact.replacementRequired ? "Yes" : "No"}</strong>
                </div>
              </div>

              <div className="cmts-job-impact__list">
                {impact.impactedEntities.length === 0 ? (
                  <p>No connected records were reported.</p>
                ) : (
                  impact.impactedEntities.map((entity) => (
                    <article key={`${entity.entityType}-${entity.entityId}`}>
                      <div>
                        <strong>{entity.entityLabel}</strong>

                        <span>
                          {entity.entityType}
                          {" • "}
                          {entity.impactType}
                        </span>
                      </div>

                      <small>
                        {entity.remediation ??
                          "Review before changing this job."}
                      </small>
                    </article>
                  ))
                )}
              </div>
            </div>
          ) : null}
        </CmtsEnterpriseModal>

        <CmtsEnterpriseModal
          open={Boolean(detailsFor)}
          title="Schedule details"
          subtitle={detailsFor?.jobName}
          size="md"
          onClose={() => setDetailsFor(null)}
        >
          {detailsFor ? (
            <div className="cmts-job-details">
              <div>
                <span>Job code</span>
                <strong>{detailsFor.jobCode}</strong>
              </div>

              <div>
                <span>Cron expression</span>
                <strong>{detailsFor.cronExpression}</strong>
              </div>

              <div>
                <span>Last execution</span>
                <strong>{formatDate(detailsFor.lastRunAt)}</strong>
              </div>

              <div>
                <span>Next execution</span>
                <strong>{formatDate(detailsFor.nextRunAt)}</strong>
              </div>

              <div>
                <span>Retry / timeout</span>
                <strong>
                  {detailsFor.maxRetryCount}
                  {" retries • "}
                  {detailsFor.timeoutSeconds}
                  {" seconds"}
                </strong>
              </div>
            </div>
          ) : null}
        </CmtsEnterpriseModal>
      </div>
    </PageContainer>
  );
}
